import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, ChevronDown, Headphones, Activity, Cpu } from "lucide-react";
import { useAuth } from "../App";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToSection = (sectionId) => {
    const currentPath = window.location.pathname;
    if (currentPath !== "/") {
      navigate("/");
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    setIsOpen(false);
    setServicesOpen(false);
  };

  const handleNavLinkClick = () => {
    setIsOpen(false);
    setServicesOpen(false);
  };

  const servicesItems = [
    { name: "Diagnostic Tool", path: "/diagnostic", icon: Activity },
    { name: "Remote Support", path: "/remote-support", icon: Headphones },
    { name: "PC Builder AI", path: "/pc-builder", icon: Cpu },
    { name: "Assist Pro", path: "/assist-pro", icon: Headphones },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-slate-950/98 backdrop-blur-xl border-b border-slate-800/50' : 'bg-slate-950/95 backdrop-blur-lg'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[75px]">
          {/* Logo - Left - Clicks navigates home and scrolls to top */}
          <button
            onClick={() => {
              navigate('/');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-2 flex-shrink-0 cursor-pointer" 
            data-testid="logo-link"
          >
            <span className="text-2xl font-bold text-white font-['Poppins']">
              Smart<span className="text-blue-500">IT</span>
            </span>
          </button>

          {/* Desktop Navigation - Center */}
          <div className="hidden lg:flex items-center justify-center flex-1 gap-[32px]">
            {/* Services - Scrolls to services section */}
            <button 
              onClick={() => scrollToSection('services')}
              className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap"
              data-testid="nav-services"
            >
              Services
            </button>
            <Link 
              to="/printing-services"
              onClick={handleNavLinkClick}
              className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap"
              data-testid="nav-printing"
            >
              Printing Services
            </Link>
            <Link 
              to="/assist-pro"
              onClick={handleNavLinkClick}
              className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap"
              data-testid="nav-assist-pro"
            >
              Assist Pro
            </Link>
            <Link 
              to="/tutorials"
              onClick={handleNavLinkClick}
              className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap"
              data-testid="nav-tutorials"
            >
              Tutorials
            </Link>
            <Link 
              to="/reviews"
              onClick={handleNavLinkClick}
              className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap"
              data-testid="nav-reviews"
            >
              Reviews
            </Link>
            <Link 
              to="/about-smartit"
              onClick={handleNavLinkClick}
              className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap"
              data-testid="nav-about"
            >
              About SmartIT
            </Link>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter'] whitespace-nowrap"
              data-testid="nav-contact"
            >
              Contact
            </button>
          </div>

          {/* Open a Ticket Button - Right */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-3">
                <Link 
                  to={user.role === "admin" ? "/admin" : "/dashboard"}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold text-[15px] py-2.5 px-5 rounded-[7px] transition-all font-['Inter'] shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30"
                  data-testid="nav-dashboard"
                >
                  {user.role === "admin" ? "Admin Panel" : "Open a Ticket"}
                </Link>
                <button 
                  onClick={logout} 
                  className="flex items-center gap-1.5 text-white/90 hover:text-[#4da3ff] transition-colors text-[15px] font-semibold font-['Inter']"
                  data-testid="nav-logout"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            ) : (
              <Link 
                to="/dashboard" 
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold text-[15px] py-2.5 px-5 rounded-[7px] transition-all font-['Inter'] shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30"
                data-testid="nav-customer-portal"
              >
                Open a Ticket
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-white p-2 hover:bg-slate-800/50 rounded-lg transition-colors" 
            onClick={() => setIsOpen(!isOpen)} 
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-slate-900/98 backdrop-blur-xl border-t border-slate-800/50"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-1">
              {/* Services - Scrolls to services section */}
              <button 
                onClick={() => scrollToSection('services')}
                className="block w-full text-left py-3 text-slate-300 hover:text-white font-medium transition-colors"
                data-testid="mobile-services"
              >
                Services
              </button>
              <Link 
                to="/printing-services"
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 text-slate-300 hover:text-white font-medium transition-colors"
                data-testid="mobile-printing"
              >
                Printing Services
              </Link>
              <Link 
                to="/assist-pro"
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 text-slate-300 hover:text-white font-medium transition-colors"
                data-testid="mobile-assist-pro"
              >
                Assist Pro
              </Link>
              <Link 
                to="/tutorials"
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 text-slate-300 hover:text-white font-medium transition-colors"
                data-testid="mobile-tutorials"
              >
                Tutorials
              </Link>
              <Link 
                to="/reviews"
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 text-slate-300 hover:text-white font-medium transition-colors"
                data-testid="mobile-reviews"
              >
                Reviews
              </Link>
              <Link 
                to="/about-smartit"
                onClick={() => setIsOpen(false)}
                className="block w-full text-left py-3 text-slate-300 hover:text-white font-medium transition-colors"
                data-testid="mobile-about"
              >
                About SmartIT
              </Link>
              <button 
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left py-3 text-slate-300 hover:text-white font-medium transition-colors"
                data-testid="mobile-contact"
              >
                Contact
              </button>

              {/* Mobile User Actions */}
              <div className="pt-4 mt-4 border-t border-slate-800/50">
                {user ? (
                  <div className="space-y-3">
                    <Link 
                      to={user.role === "admin" ? "/admin" : "/dashboard"}
                      className="block w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 px-5 rounded-lg text-center transition-all"
                      onClick={() => setIsOpen(false)}
                      data-testid="mobile-dashboard"
                    >
                      {user.role === "admin" ? "Admin Panel" : "Open a Ticket"}
                    </Link>
                    <button 
                      onClick={() => { logout(); setIsOpen(false); }} 
                      className="flex items-center justify-center gap-2 w-full py-3 text-slate-400 hover:text-white transition-colors"
                      data-testid="mobile-logout"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                ) : (
                  <Link 
                    to="/dashboard" 
                    className="block w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 px-5 rounded-lg text-center transition-all shadow-lg shadow-blue-600/25"
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-customer-portal"
                  >
                    Open a Ticket
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
